import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ScrollToTop } from "@/components/ScrollToTop";
import { lazy, Suspense } from "react";

// Eager load homepage for better UX
import Index from "./pages/Index";

// Lazy load all other pages
const BookDemo = lazy(() => import("./pages/BookDemo"));
const PreQual = lazy(() => import("./pages/PreQual"));
const Guides = lazy(() => import("./pages/Guides"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const AcceptableUse = lazy(() => import("./pages/AcceptableUse"));
const DataProcessingAddendum = lazy(() => import("./pages/DataProcessingAddendum"));
const CustomerProofAutomation = lazy(() => import("./pages/CustomerProofAutomation"));
const LLMSnippets = lazy(() => import("./pages/LLMSnippets"));
const GTMWithProof = lazy(() => import("./pages/guides/GTMWithProof"));
const CustomerProofFlywheel = lazy(() => import("./pages/guides/CustomerProofFlywheel"));
const CustomerStoryCollection = lazy(() => import("./pages/guides/CustomerStoryCollection"));
const TestimonialAutomation = lazy(() => import("./pages/guides/TestimonialAutomation"));
const CaseStudyPlaybook = lazy(() => import("./pages/guides/CaseStudyPlaybook"));
const GenerateSalesCollateralAI = lazy(() => import("./pages/guides/GenerateSalesCollateralAI"));
const VOCAIMethods = lazy(() => import("./pages/guides/VOCAIMethods"));
const AICaseStudyTools = lazy(() => import("./pages/guides/AICaseStudyTools"));
const TellCaseyVsAgencies = lazy(() => import("./pages/guides/TellCaseyVsAgencies"));
const TellCaseyVsOthers = lazy(() => import("./pages/guides/TellCaseyVsOthers"));
const TrustBeforeFeatures = lazy(() => import("./pages/guides/TrustBeforeFeatures"));
const FutureOfCustomerMarketing = lazy(() => import("./pages/guides/FutureOfCustomerMarketing"));
const CustomerInterviewScript = lazy(() => import("./pages/guides/CustomerInterviewScript"));
const CaseStudyTemplate = lazy(() => import("./pages/guides/CaseStudyTemplate"));
const TestimonialTemplateLibrary = lazy(() => import("./pages/guides/TestimonialTemplateLibrary"));
const CustomerProofSOP = lazy(() => import("./pages/guides/CustomerProofSOP"));
const Glossary = lazy(() => import("./pages/Glossary"));
const Templates = lazy(() => import("./pages/Templates"));
const Playbooks = lazy(() => import("./pages/Playbooks"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const WhatIsCustomerProofAutomation = lazy(() => import("./pages/WhatIsCustomerProofAutomation"));
const Resources = lazy(() => import("./pages/Resources"));
const MarketResearchInsights = lazy(() => import("./pages/MarketResearchInsights"));
// HospitalityAI removed
const Schedule = lazy(() => import("./pages/Schedule"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const Partners = lazy(() => import("./pages/Partners"));
const TryCasey = lazy(() => import("./pages/TryCasey"));
const WebsiteSpeaks = lazy(() => import("./pages/WebsiteSpeaks"));
const LeadQualification = lazy(() => import("./pages/LeadQualification"));
const VoiceLayer = lazy(() => import("./pages/VoiceLayer"));
const WhyLeadFormsAreDying = lazy(() => import("./pages/WhyLeadFormsAreDying"));
const CaseyIntelligence = lazy(() => import("./pages/CaseyIntelligence"));
const HomepageV2 = lazy(() => import("./pages/HomepageV2"));
const HomepageV3 = lazy(() => import("./pages/HomepageV3"));
const HomepageV4 = lazy(() => import("./pages/HomepageV4"));
const HomepageV5 = lazy(() => import("./pages/HomepageV5"));
const HomepageV6 = lazy(() => import("./pages/HomepageV6"));
const HomepageV7 = lazy(() => import("./pages/HomepageV7"));
const HomepageV8 = lazy(() => import("./pages/HomepageV8"));
const HomepageV9 = lazy(() => import("./pages/HomepageV9"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const HelpCategory = lazy(() => import("./pages/HelpCategory"));
const HelpArticle = lazy(() => import("./pages/HelpArticle"));
const NotFound = lazy(() => import("./pages/NotFound"));
const N8nBuilders = lazy(() => import("./pages/N8nBuilders"));
const CustomerInterviews = lazy(() => import("./pages/CustomerInterviews"));
const UserResearch = lazy(() => import("./pages/UserResearch"));
const UserResearchInsightsExample = lazy(() => import("./pages/UserResearchInsightsExample"));
const CaseStudies = lazy(() => import("./pages/CaseStudies"));
const FlowtaskCaseStudy = lazy(() => import("./pages/FlowtaskCaseStudy"));
const ManagedCustomerStories = lazy(() => import("./pages/ManagedCustomerStories"));
const ManagedNPS = lazy(() => import("./pages/ManagedNPS"));
const CustomerStoriesAutomation = lazy(() => import("./pages/content/CustomerStoriesAutomation"));
const AutomatedCaseStudies = lazy(() => import("./pages/content/AutomatedCaseStudies"));
const AutomatedCustomerInterviews = lazy(() => import("./pages/content/AutomatedCustomerInterviews"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>}>
            <Routes>
              <Route path="/" element={<HomepageV3 />} />
              <Route path="/early-access" element={<BookDemo />} />
              <Route path="/book-demo" element={<BookDemo />} />
              <Route path="/pre-qual" element={<PreQual />} />
              <Route path="/guides" element={<Guides />} />
              <Route path="/guides/customer-proof-automation" element={<CustomerProofAutomation />} />
              <Route path="/llm-snippets" element={<LLMSnippets />} />
              <Route path="/guides/gtm-with-proof" element={<GTMWithProof />} />
              <Route path="/guides/customer-proof-flywheel" element={<CustomerProofFlywheel />} />
              <Route path="/guides/customer-story-collection" element={<CustomerStoryCollection />} />
              <Route path="/guides/testimonial-automation" element={<TestimonialAutomation />} />
              <Route path="/guides/case-study-playbook" element={<CaseStudyPlaybook />} />
              <Route path="/guides/generate-sales-collateral-ai" element={<GenerateSalesCollateralAI />} />
              <Route path="/guides/voc-ai-methods" element={<VOCAIMethods />} />
              <Route path="/guides/ai-case-study-tools" element={<AICaseStudyTools />} />
              <Route path="/guides/tellcasey-vs-agencies" element={<TellCaseyVsAgencies />} />
              <Route path="/guides/tellcasey-vs-others" element={<TellCaseyVsOthers />} />
              <Route path="/guides/trust-before-features" element={<TrustBeforeFeatures />} />
              <Route path="/guides/future-of-customer-marketing" element={<FutureOfCustomerMarketing />} />
              <Route path="/guides/customer-interview-script" element={<CustomerInterviewScript />} />
              <Route path="/guides/case-study-template" element={<CaseStudyTemplate />} />
              <Route path="/guides/testimonial-template-library" element={<TestimonialTemplateLibrary />} />
              <Route path="/guides/customer-proof-sop" element={<CustomerProofSOP />} />
              <Route path="/glossary" element={<Glossary />} />
              <Route path="/templates" element={<Templates />} />
              <Route path="/playbooks" element={<Playbooks />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/what-is-customer-proof-automation" element={<WhatIsCustomerProofAutomation />} />
              <Route path="/resources" element={<Resources />} />
              <Route path="/solutions/market-research-ai" element={<MarketResearchInsights />} />
              <Route path="/mr" element={<Navigate to="/solutions/market-research-ai" replace />} />
              {/* Hospitality route removed */}
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/partners" element={<Partners />} />
              <Route path="/try" element={<TryCasey />} />
              <Route path="/website-voice-widget" element={<WebsiteSpeaks />} />
              <Route path="/let-your-website-speak-for-itself" element={<Navigate to="/website-voice-widget" replace />} />
              <Route path="/voice-layer" element={<VoiceLayer />} />
              <Route path="/lead-qualification" element={<LeadQualification />} />
              <Route path="/resources/why-lead-forms-are-dying" element={<WhyLeadFormsAreDying />} />
              <Route path="/casey-intelligence" element={<CaseyIntelligence />} />
              <Route path="/services/customer-stories" element={<ManagedCustomerStories />} />
              <Route path="/managed-customer-stories" element={<Navigate to="/services/customer-stories" replace />} />
              <Route path="/services/nps" element={<ManagedNPS />} />
              <Route path="/content/customer-stories/customer-stories-automation" element={<CustomerStoriesAutomation />} />
              <Route path="/content/customer-stories/automated-case-studies" element={<AutomatedCaseStudies />} />
              <Route path="/content/customer-stories/automated-customer-interviews" element={<AutomatedCustomerInterviews />} />
              <Route path="/managed-nps" element={<Navigate to="/services/nps" replace />} />
              <Route path="/schedule" element={<Schedule />} />
              <Route path="/root-v1" element={<Navigate to="/" replace />} />
              <Route path="/root-v3" element={<HomepageV2 />} />
              <Route path="/root-v4" element={<HomepageV4 />} />
              <Route path="/root-v5" element={<HomepageV5 />} />
              <Route path="/root-v6" element={<HomepageV6 />} />
              <Route path="/root-v7" element={<HomepageV7 />} />
              <Route path="/root-v8" element={<HomepageV8 />} />
              <Route path="/root-v9" element={<HomepageV9 />} />
              <Route path="/legal/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/legal/terms-of-service" element={<TermsOfService />} />
              <Route path="/legal/acceptable-use" element={<AcceptableUse />} />
              <Route path="/legal/dpa" element={<DataProcessingAddendum />} />
              <Route path="/help-center" element={<HelpCenter />} />
              <Route path="/tellcasey-for-n8n-builders" element={<N8nBuilders />} />
              <Route path="/usecases/customer-stories" element={<CustomerInterviews />} />
              <Route path="/usecases/customer-interviews" element={<Navigate to="/usecases/customer-stories" replace />} />
              <Route path="/usecases/user-research" element={<UserResearch />} />
              <Route path="/case-studies" element={<CaseStudies />} />
              <Route path="/case-studies/brightnest-customer-research" element={<UserResearchInsightsExample />} />
              <Route path="/case-studies/flowtask-user-research" element={<FlowtaskCaseStudy />} />
              <Route path="/resources/user-research-insights-example" element={<Navigate to="/case-studies/brightnest-customer-research" replace />} />
              <Route path="/help-center/category/:slug" element={<HelpCategory />} />
              <Route path="/help-center/:slug" element={<HelpArticle />} />
              {/* Redirect old /help routes */}
              <Route path="/help" element={<Navigate to="/help-center" replace />} />
              <Route path="/help/:slug" element={<Navigate to="/help-center" replace />} />
              <Route path="/contact-us" element={<ContactUs />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
